import React, { useState, useEffect, useCallback } from 'react';
import { ChatDisplay, MessageInput } from '@components/GenerateContracts';
import { DocumentsTab } from '@components/EmbedDocuments';
import { AnalyticsTab } from '@components/EmbedDocuments/AnalyticsTab';
import {
  Container,
  StyledTabs,
  StyledTab,
} from './EmbedDocumentsContainer.styles';
import { documentEmbeddingApi } from '@api/api';
import { FileViewerModal } from '@components/Shared/FileViewerModal';

export default function EmbedDocumentsContainer() {
  const [activeTab, setActiveTab] = useState(0);
  const [retrievedDocuments, setRetrievedDocuments] = useState([]);
  const [retrievedAnalytics, setRetrievedAnalytics] = useState(null);
  const [isLoadingDocuments, setIsLoadingDocuments] = useState(true);
  const [chatMessages, setChatMessages] = useState([]);
  const [isChatLoading, setIsChatLoading] = useState(false);
  const [chatError, setChatError] = useState(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [fileIdToView, setFileIdToView] = useState(null);
  const [initialDocInfoForModal, setInitialDocInfoForModal] = useState({});

  useEffect(() => {
    fetchDocuments();
    fetchAnalytics();
  }, []);

  const handleTabChange = (_event, newValue) => {
    setActiveTab(newValue);
  };

  const fetchDocuments = useCallback(async () => {
    setIsLoadingDocuments(true);
    try {
      const docs = await documentEmbeddingApi.getDocuments();
      setRetrievedDocuments(Array.isArray(docs) ? docs : []);
    } catch (error) {
      console.error('Failed to fetch documents:', error);
      setRetrievedDocuments([]);
    } finally {
      setIsLoadingDocuments(false);
    }
  }, []);

  async function fetchAnalytics() {
    try {
      const analytics = await documentEmbeddingApi.getAnalytics();
      setRetrievedAnalytics(analytics);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
      setRetrievedAnalytics(null);
    }
  }

  const handleOpenViewModalFromTab = useCallback((doc) => {
    if (!doc || !doc.id) {
      console.error("EmbedDocumentsContainer: Invalid document data received from tab:", doc);
      return;
    }
    setFileIdToView(doc.id);
    setInitialDocInfoForModal({ name: doc.name, file_type: doc.file_type });
    setIsViewModalOpen(true);
  }, []);

  const handleOpenViewModalFromCitation = useCallback((identifier) => {
    if (!identifier) {
      console.error("EmbedDocumentsContainer: Attempted to open view modal with invalid identifier:", identifier);
      return;
    }

    let resolvedFileId = null;
    let foundDocInfo = {};

    const looksLikeFilename = /\./.test(identifier) && !/^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(identifier);

    if (looksLikeFilename) {
      const matchedDoc = retrievedDocuments.find(doc => doc.name === identifier);
      if (matchedDoc) {
        resolvedFileId = matchedDoc.id;
        foundDocInfo = { name: matchedDoc.name, file_type: matchedDoc.file_type };
      } else {
        console.warn(`EmbedDocumentsContainer: Could not find a document with filename "${identifier}" in the list.`);
        alert(`Error: Could not find document named "${identifier}".`);
        return;
      }
    } else {
      resolvedFileId = identifier;
      const matchedDoc = retrievedDocuments.find(doc => doc.id === resolvedFileId);
      if (matchedDoc) {
        foundDocInfo = { name: matchedDoc.name, file_type: matchedDoc.file_type };
      }
    }

    if (resolvedFileId) {
      setFileIdToView(resolvedFileId);
      setInitialDocInfoForModal(foundDocInfo);
      setIsViewModalOpen(true);
    } else {
      console.error(`EmbedDocumentsContainer: Could not resolve identifier "${identifier}" to a file ID.`);
    }
  }, [retrievedDocuments]);

  const handleCloseViewModal = useCallback(() => {
    setIsViewModalOpen(false);
    setFileIdToView(null);
    setInitialDocInfoForModal({});
  }, []);

  const handleSendChatMessage = async (inputText) => {
    if (!inputText.trim()) return;

    const userMessage = { role: 'user', content: inputText, timestamp: new Date().toISOString(), id: `user-${Date.now()}` };
    setChatMessages(prevMessages => [...prevMessages, userMessage]);
    setIsChatLoading(true);
    setChatError(null);
    const loadingMessage = { role: 'assistant', type: 'loading', id: `loading-${Date.now()}` };
    setChatMessages(prevMessages => [...prevMessages, loadingMessage]);

    try {
      const response = await documentEmbeddingApi.chat(inputText);

      let assistantMessageContent = "Sorry, I couldn't process that response.";
      let assistantRole = 'assistant';

      if (response && typeof response.response === 'string' && response.response.trim()) {
        try {
          assistantMessageContent = JSON.parse(response.response);
        } catch (parseError) {
          assistantMessageContent = response.response;
          assistantRole = 'system';
        }
      } else {
        if (response && response.error) {
          assistantMessageContent = `Error: ${response.error}`;
        }
        assistantRole = 'system';
      }

      const assistantMessage = {
        role: assistantRole,
        content: assistantMessageContent,
        timestamp: new Date().toISOString(),
        id: `assistant-${Date.now()}`
      };

      setChatMessages(prevMessages =>
        prevMessages.filter(msg => msg.type !== 'loading').concat(assistantMessage)
      );

    } catch (err) {
      console.error('Error sending chat message:', err);
      setChatError(`Failed to get response: ${err.message}`);
      const errorMessage = { role: 'system', type: 'error', content: `Failed to get response: ${err.message}`, timestamp: new Date().toISOString(), id: `error-${Date.now()}` };
      setChatMessages(prevMessages =>
        prevMessages.filter(msg => msg.type !== 'loading').concat(errorMessage)
      );
    } finally {
      setIsChatLoading(false);
    }
  };

  return (
    <Container>
      <StyledTabs value={activeTab} onChange={handleTabChange}>
        <StyledTab label="Documents" />
        <StyledTab label="Chat" />
        <StyledTab label="Analytics" />
      </StyledTabs>

      {activeTab === 0 && (
        <DocumentsTab
          documents={retrievedDocuments}
          isLoading={isLoadingDocuments}
          refreshDocuments={fetchDocuments}
          onViewDocument={handleOpenViewModalFromTab}
        />
      )}
      {activeTab === 1 && (
        <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 200px)' }}>
          <ChatDisplay
            messages={chatMessages}
            placeholderMessage="Dig deeper into your private document repository."
            onCitationClick={handleOpenViewModalFromCitation}
          />
          <MessageInput onSendMessage={handleSendChatMessage} isLoading={isChatLoading} />
          {chatError && <div style={{ color: 'red', padding: '10px' }}>{chatError}</div>}
        </div>
      )}
      {activeTab === 2 && <AnalyticsTab analytics={retrievedAnalytics} />}

      <FileViewerModal
        open={isViewModalOpen}
        onClose={handleCloseViewModal}
        fileId={fileIdToView}
        initialDocInfo={initialDocInfoForModal}
      />
    </Container>
  );
};